import React, { useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Loader2, Sparkles, FileSearch, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AIToolsPage() {
  const { t } = useI18n();
  return (
    <div className="space-y-5" data-testid="ai-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight">{t("aiTools")}</h1>
        <p className="text-sm text-muted-foreground">Powered by Emergent LLM Key</p>
      </div>
      <Tabs defaultValue="contract">
        <TabsList>
          <TabsTrigger value="contract" data-testid="tab-ai-contract"><FileSearch className="w-3 h-3 mr-1" />{t("aiContractAnalyzer")}</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-ai-summary"><FileText className="w-3 h-3 mr-1" />{t("aiSummarize")}</TabsTrigger>
        </TabsList>
        <TabsContent value="contract" className="mt-5"><ContractTool /></TabsContent>
        <TabsContent value="summary" className="mt-5"><SummarizeTool /></TabsContent>
      </Tabs>
    </div>
  );
}

function ContractTool() {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/contract-analysis", { text });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setLoading(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> {t("aiContractAnalyzer")}</h3>
        <Textarea
          data-testid="ai-contract-text"
          placeholder={t("pasteContract")}
          value={text}
          onChange={e => setText(e.target.value)}
          rows={14}
        />
        <Button data-testid="ai-contract-run" onClick={run} disabled={loading || !text.trim()} className="mt-3 w-full">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("runAnalysis")} <Sparkles className="w-4 h-4 ml-1" /></>}
        </Button>
      </Card>
      <Card className="p-5 border-border/60">
        {!res ? (
          <div className="text-sm text-muted-foreground text-center py-14">Run analysis to see results</div>
        ) : (
          <div className="space-y-4" data-testid="ai-contract-result">
            <div>
              <h4 className="font-semibold text-sm mb-1">{t("summary")}</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{res.summary}</p>
            </div>
            {res.key_terms && (
              <div>
                <h4 className="font-semibold text-sm mb-2">{t("keyTerms")}</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(res.key_terms).map(([k, v]) => (
                    <div key={k} className="p-2 rounded border border-border/60">
                      <div className="uppercase tracking-wider text-muted-foreground font-mono-brand text-[10px]">{k}</div>
                      <div className="font-medium">{String(v || "—")}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {res.risks?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">{t("risks")}</h4>
                <ul className="space-y-2 text-sm">
                  {res.risks.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 p-2 rounded border border-border/60">
                      <Badge variant={r.severity === "high" ? "destructive" : "secondary"}>{r.severity}</Badge>
                      <div><div className="font-medium">{r.clause}</div><div className="text-muted-foreground text-xs">{r.note}</div></div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {res.action_items?.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm mb-2">{t("actionItems")}</h4>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  {res.action_items.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

function SummarizeTool() {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState("");

  const run = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/summarize", { text });
      setRes(data.summary);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    setLoading(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 border-border/60">
        <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> {t("aiSummarize")}</h3>
        <Textarea data-testid="ai-sum-text" value={text} onChange={e => setText(e.target.value)} rows={14} />
        <Button data-testid="ai-sum-run" onClick={run} disabled={loading} className="mt-3 w-full">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("runAnalysis")}</>}
        </Button>
      </Card>
      <Card className="p-5 border-border/60">
        {!res ? <div className="text-sm text-muted-foreground text-center py-14">Summary will appear here</div> : (
          <div className="whitespace-pre-wrap text-sm" data-testid="ai-sum-result">{res}</div>
        )}
      </Card>
    </div>
  );
}
